// src/components/ReportsDashboard.js

import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, RadialBarChart, RadialBar, PolarAngleAxis, AreaChart, Area } from 'recharts';
import { fetchAccountingCharts, fetchAccountingDashboardPdf } from '../api';
import { formatCurrency } from '../utils/currency';
import { canAccessTab } from '../utils/rbac';

// Helper component for permission denied message, styled with Tailwind CSS
const PermissionDenied = () => (
    <div className="p-6 text-center bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="bg-white shadow-md rounded-lg max-w-md mx-auto p-8">
            {/* Using an inline SVG for the icon to avoid dependency issues */}
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16">
              <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
            </svg>
            <h2 className="text-2xl font-bold text-red-600 mb-2">Access Denied</h2>
            <p className="text-gray-600">You do not have the necessary permissions to view this page.</p>
        </div>
    </div>
);

// A revised color palette for a more professional look
const COLORS = ['#2563eb', '#16a34a', '#64748b', '#f59e0b', '#4f46e5', '#d97706'];
const UTILIZATION_COLOR = '#2563eb'; // Tailwind's blue-600

// Reusable component for displaying key statistics in a visually appealing card, styled with Tailwind CSS
const StatCard = ({ title, value, icon, currency = '', unit = '' }) => (
    <div className="bg-white shadow-md rounded-lg p-6 flex flex-col items-center justify-center text-center h-full">
         <div className={`flex items-center justify-center h-16 w-16 rounded-full mb-4 ${icon.bgColor}`}>
            {/* Using an inline SVG for the icon */}
            <svg className={`w-8 h-8 text-white`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={icon.path} />
            </svg>
        </div>
        <h5 className="text-sm font-medium text-gray-500 uppercase tracking-wider">{title}</h5>
        <p className="text-3xl font-bold text-gray-900 mt-1">
            {currency}{value}{unit}
        </p>
    </div>
);


function ReportsDashboard({
  authAxios,
  financialReports,
  caseloadReports,
  performanceMetrics,
  insightsReports,
  userRole,
  // --- NEW: Props for date filtering ---
  reportDateRange,
  setReportDateRange,
}) {
  const dashboardRef = useRef(null);
  const [acctCharts, setAcctCharts] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // ACCOUNTANT was missing from canViewPage, so the entire Reports page
  // short-circuited to <PermissionDenied/> for the one role whose job it most
  // obviously is — even though TAB_ACCESS.reports grants it, App.js's
  // canViewFinancialReports includes it, and the backend's
  // IsFinanceReportViewer allows it. canViewAccounting below already listed
  // ACCOUNTANT but was unreachable behind the page gate. Same
  // sidebar-says-yes / component-says-no drift already documented twice in
  // training-docs/_meta/permission-audit-findings.md. [F-1]
  //
  // [WF-B10] It recurred a third time for AUDITOR — auditor_allowed_views
  // already grants real read access to every report this dashboard uses,
  // and TAB_ACCESS.reports already includes AUDITOR, but this hand-
  // maintained array didn't. Delegating to canAccessTab()/TAB_ACCESS
  // directly (instead of adding 'AUDITOR' to a third hand-maintained copy)
  // is the structural fix that makes a fourth recurrence impossible.
  // [WF-B11] AUDITOR added to Financials/Insights/Caseload/Performance —
  // canViewPage now reaches AUDITOR (WF-B10 above), but these four finer-
  // grained section gates predate AUDITOR existing as a role and would
  // otherwise leave every section blank once the page itself became
  // reachable. Verified against the backend's auditor_allowed_views
  // whitelist (legal_ops/permissions.py) before adding: FinancialReportsView,
  // CaseLoadReportsView, PerformanceMetricsView and InsightsReportsView are
  // all listed — the exact four endpoints these four flags gate. Deliberately
  // NOT added to canViewAccounting: its chart data comes from
  // AccountingDashboardChartsView, which is NOT in that whitelist, so
  // granting it here would just 403 silently (loadAcctCharts' own
  // catch-and-ignore) rather than show real data.
  const permissions = useMemo(() => ({
      canViewPage: canAccessTab(userRole, 'reports'),
      canViewFinancials: ['PARTNER', 'BOARD_MEMBER', 'SUPERUSER', 'ACCOUNTANT', 'AUDITOR'].includes(userRole),
      canViewInsights: ['PARTNER', 'BOARD_MEMBER', 'SUPERUSER', 'ACCOUNTANT', 'AUDITOR'].includes(userRole),
      canViewCaseload: userRole === 'PARTNER' || userRole === 'SUPERUSER' || userRole === 'AUDITOR',
      canViewPerformance: ['PARTNER', 'ASSOCIATE', 'SUPERUSER', 'AUDITOR'].includes(userRole),
      canViewAccounting: ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole),
  }), [userRole]);

  // Fetch accounting chart data
  const loadAcctCharts = useCallback(async () => {
    if (!authAxios) return;
    try {
      const params = {};
      if (reportDateRange?.startDate) params.start_date = reportDateRange.startDate;
      if (reportDateRange?.endDate) params.end_date = reportDateRange.endDate;
      const data = await fetchAccountingCharts(authAxios, params);
      setAcctCharts(data);
    } catch { /* silently ignore if endpoint not yet deployed */ }
  }, [authAxios, reportDateRange]);

  useEffect(() => { if (permissions.canViewAccounting) loadAcctCharts(); }, [permissions.canViewAccounting, loadAcctCharts]);

  // --- Handler for date changes ---
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setReportDateRange(prev => ({ ...prev, [name]: value }));
  };

  const revenueByCaseTypeData = insightsReports.revenue_by_case_type?.map(item => ({
    name: item.invoice__case__case_type || "Uncategorized",
    revenue: parseFloat(item.total_revenue)
  })) || [];

  const utilizationData = [{
      name: 'Utilization',
      value: insightsReports.utilization_rate || 0,
  }];

  const clientAcquisitionData = insightsReports.client_acquisition ? [
      { name: 'New Clients', value: insightsReports.client_acquisition.new_clients_last_year },
      { name: 'Recurring Clients', value: insightsReports.client_acquisition.recurring_clients_last_year },
  ] : [];
  
  const profitabilityData = insightsReports.profitability_per_case || [];
  const clvData = insightsReports.client_lifetime_value || [];

  const handleExportPDF = async () => {
    if (!authAxios) return;
    setPdfLoading(true);
    try {
      const params = {};
      if (reportDateRange?.startDate) params.start_date = reportDateRange.startDate;
      if (reportDateRange?.endDate) params.end_date = reportDateRange.endDate;
      const blob = await fetchAccountingDashboardPdf(authAxios, params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'LegalOps_Reports_Dashboard.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const currencyTooltipFormatter = (value) => formatCurrency(value);

  if (!permissions.canViewPage) {
    return <PermissionDenied />;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen" ref={dashboardRef}>
      <div className="flex flex-wrap justify-between items-center mb-6 pb-4 border-b border-gray-200 gap-4">
        <h2 className="text-3xl font-bold text-gray-800">Reports Dashboard</h2>
        <div className="flex items-center gap-4">
            {/* --- NEW: Date filter inputs --- */}
            <div className="flex items-center gap-2">
                <label htmlFor="startDate" className="text-sm font-medium text-gray-700">From:</label>
                <input 
                    type="date" 
                    id="startDate"
                    name="startDate"
                    value={reportDateRange.startDate}
                    onChange={handleDateChange}
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
            </div>
            <div className="flex items-center gap-2">
                <label htmlFor="endDate" className="text-sm font-medium text-gray-700">To:</label>
                <input 
                    type="date" 
                    id="endDate"
                    name="endDate"
                    value={reportDateRange.endDate}
                    onChange={handleDateChange}
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                />
            </div>
            <button 
              className="bg-white text-gray-700 px-4 py-2 rounded-md shadow-sm border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              onClick={handleExportPDF}
              disabled={pdfLoading}
            >
              {pdfLoading ? 'Generating...' : 'Export PDF'}
            </button>
        </div>
      </div>

      {permissions.canViewFinancials && (
        <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-700 mb-4">Financial Overview</h3>
            {/* --- MODIFIED: Grid layout to accommodate 5 cards --- */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                <StatCard 
                    title="Total Invoiced" 
                    value={formatCurrency(parseFloat(financialReports.total_invoiced_amount || 0))} 
                    icon={{ path: "M9 14l6-6-6-6", bgColor: 'bg-blue-600' }}
                />
                <StatCard 
                    title="Total Paid" 
                    value={formatCurrency(parseFloat(financialReports.paid_invoices_amount || 0))}
                    icon={{ path: "M5 13l4 4L19 7", bgColor: 'bg-green-600' }}
                />
                <StatCard 
                    title="Outstanding" 
                    value={formatCurrency(parseFloat(financialReports.outstanding_invoices_amount || 0))}
                    icon={{ path: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z", bgColor: 'bg-purple-600' }}
                />
                {/* --- NEW: WIP StatCard --- */}
                <StatCard 
                    title="Work-In-Progress (WIP)" 
                    value={formatCurrency(parseFloat(financialReports.work_in_progress_amount || 0))}
                    icon={{ path: "M19.428 15.428a2 2 0 00-1.049-.548l-1.875-.469a2 2 0 01-1.049-.548l-1.875-.469a2 2 0 00-2.098 1.098l-.938 1.875a2 2 0 01-3.468-.866l-.937-1.875a2 2 0 00-2.098-1.098l-1.875.469a2 2 0 01-1.049.548l-1.875.469a2 2 0 00-1.049.548A2 2 0 002 17.528V19a2 2 0 002 2h16a2 2 0 002-2v-1.472a2 2 0 00-2.572-1.999zM14 6a4 4 0 10-8 0 4 4 0 008 0z", bgColor: 'bg-orange-500' }}
                />
                <StatCard 
                    title="Avg. Invoice Age (DSO)" 
                    value={insightsReports.average_invoice_age_days || 0}
                    icon={{ path: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z", bgColor: 'bg-yellow-500' }}
                    unit=" days"
                />
            </div>
        </div>
      )}
      
      {permissions.canViewInsights && (
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-gray-700 mb-4">Key Performance Insights</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="bg-white shadow-md rounded-lg p-6 h-full">
              <h5 className="text-base font-semibold text-gray-700 mb-4">Revenue by Case Type</h5>
              <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenueByCaseTypeData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={currencyTooltipFormatter} />
                      <Legend />
                      <Bar dataKey="revenue" fill={COLORS[0]} />
                  </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white shadow-md rounded-lg p-6 h-full">
              <h5 className="text-base font-semibold text-gray-700 mb-4">Most Profitable Cases (Top 10)</h5>
              <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={profitabilityData} layout="vertical" margin={{ top: 5, right: 20, left: 120, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="case_title" type="category" width={120} />
                      <Tooltip formatter={currencyTooltipFormatter} />
                      <Legend />
                      <Bar dataKey="profit" fill={COLORS[4]} />
                  </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-gray-700 mb-4">Productivity & Efficiency</h3>
           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
             <div className="bg-white shadow-md rounded-lg p-6 flex flex-col items-center justify-center h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">Firm Utilization Rate</h5>
                <ResponsiveContainer width="100%" height={250}>
                    <RadialBarChart innerRadius="80%" outerRadius="100%" data={utilizationData} startAngle={90} endAngle={-270}>
                        <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                        <RadialBar background clockWise dataKey='value' cornerRadius={10} fill={UTILIZATION_COLOR} />
                        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fontSize="2rem" fontWeight="bold" fill={UTILIZATION_COLOR}>
                            {`${insightsReports.utilization_rate || 0}%`}
                        </text>
                    </RadialBarChart>
                </ResponsiveContainer>
            </div>
            <div className="lg:col-span-1">
              <StatCard 
                  title="Avg. Case Lifecycle" 
                  value={insightsReports.average_case_lifecycle_days || 0}
                  icon={{ path: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z", bgColor: 'bg-green-600' }}
                  unit=" days"
              />
            </div>
            <div className="bg-white shadow-md rounded-lg p-6 h-full lg:col-span-2">
              <h5 className="text-base font-semibold text-gray-700 mb-4">Client Acquisition (Last Year)</h5>
               <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie dataKey="value" data={clientAcquisitionData} outerRadius={100} label>
                        {clientAcquisitionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
            </div>
          </div>
          
          <h3 className="text-2xl font-bold text-gray-700 mb-4">Client Analysis</h3>
          <div className="grid grid-cols-1 gap-6">
             <div className="bg-white shadow-md rounded-lg p-6 h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">Client Lifetime Value (Top 10)</h5>
                <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={clvData} layout="vertical" margin={{ top: 20, right: 30, left: 100, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="client_name" type="category" width={150} />
                        <Tooltip formatter={currencyTooltipFormatter} />
                        <Legend />
                        <Bar dataKey="total_revenue" name="Total Revenue" fill={COLORS[5]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {permissions.canViewCaseload && (
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-700 mb-4">Caseload Overview</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <StatCard 
                title="Total Cases" 
                value={caseloadReports.total_cases || 0}
                icon={{ path: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z", bgColor: 'bg-blue-600' }}
              />
              <StatCard 
                title="Open/Active" 
                value={(caseloadReports.cases_by_status || []).filter(s => ['Open','Active','Pending','On_Hold'].includes(s.status)).reduce((a,b) => a + (b.count || 0), 0)}
                icon={{ path: "M5 13l4 4L19 7", bgColor: 'bg-teal-600' }}
              />
              <StatCard 
                title="Closed" 
                value={(caseloadReports.cases_by_status || []).filter(s => s.status === 'Closed').reduce((a,b) => a + (b.count || 0), 0)}
                icon={{ path: "M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 14l-6-6", bgColor: 'bg-gray-600' }}
              />
              <StatCard 
                title="Avg. Days to Close" 
                value={caseloadReports.average_days_to_close_case || 0}
                icon={{ path: "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z", bgColor: 'bg-amber-600' }}
                unit=" days"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white shadow-md rounded-lg p-6 h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">Cases by Status</h5>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={(caseloadReports.cases_by_status || []).map(item => ({ name: item.status, count: item.count }))}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill={COLORS[2]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="bg-white shadow-md rounded-lg p-6 h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">Cases by Type</h5>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie dataKey="count" data={(caseloadReports.cases_by_type || []).map((item, idx) => ({ name: item.case_type || 'Unspecified', count: item.count, fill: COLORS[idx % COLORS.length] }))} outerRadius={100} label>
                      {(caseloadReports.cases_by_type || []).map((_, index) => (
                        <Cell key={`case-type-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
      )}

      {permissions.canViewPerformance && (
          <div className="mb-8">
            <h3 className="text-2xl font-bold text-gray-700 mb-4">Performance Metrics</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard 
                    title="Total Time Logged" 
                    value={performanceMetrics.total_time_logged_hours || 0}
                    icon={{ path: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z", bgColor: 'bg-cyan-500' }}
                    unit=" hrs"
                />
            </div>
          </div>
      )}

      {/* Accounting Charts Section */}
      {permissions.canViewAccounting && acctCharts && (
        <div className="mb-8">
          <h3 className="text-2xl font-bold text-gray-700 mb-4">Accounting Analytics</h3>

          {/* Row 1: Income vs Expenses + AP Aging */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {acctCharts.income_vs_expenses?.length > 0 && (
              <div className="bg-white shadow-md rounded-lg p-6 h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">Income vs Expenses (Monthly)</h5>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={acctCharts.income_vs_expenses} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={currencyTooltipFormatter} />
                    <Legend />
                    <Area type="monotone" dataKey="income" stackId="1" stroke="#16a34a" fill="#bbf7d0" name="Income" />
                    <Area type="monotone" dataKey="expenses" stackId="2" stroke="#dc2626" fill="#fecaca" name="Expenses" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
            {acctCharts.ap_aging_summary?.length > 0 && (
              <div className="bg-white shadow-md rounded-lg p-6 h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">AP Aging Summary</h5>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie dataKey="total" data={acctCharts.ap_aging_summary.map((d, i) => ({ ...d, fill: COLORS[i % COLORS.length] }))} outerRadius={100} label={({ bucket, total }) => `${bucket}: ${formatCurrency(total)}`}>
                      {acctCharts.ap_aging_summary.map((_, i) => (
                        <Cell key={`ap-${i}`} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={currencyTooltipFormatter} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Row 2: Monthly Expense Breakdown + Budget vs Actual */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {acctCharts.monthly_expense_breakdown?.length > 0 && (
              <div className="bg-white shadow-md rounded-lg p-6 h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">Monthly Expense Breakdown</h5>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={acctCharts.monthly_expense_breakdown} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip formatter={currencyTooltipFormatter} />
                    <Legend />
                    <Bar dataKey="total" fill={COLORS[0]} name="Expense Total" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {acctCharts.budget_vs_actual?.length > 0 && (
              <div className="bg-white shadow-md rounded-lg p-6 h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">Budget vs Actual</h5>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={acctCharts.budget_vs_actual} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis />
                    <Tooltip formatter={currencyTooltipFormatter} />
                    <Legend />
                    <Bar dataKey="budget" fill={COLORS[2]} name="Budget" />
                    <Bar dataKey="actual" fill={COLORS[3]} name="Actual" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Row 3: Petty Cash Utilization + GL Account Balances */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {acctCharts.petty_cash_utilization?.length > 0 && (
              <div className="bg-white shadow-md rounded-lg p-6 h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">Petty Cash Utilization</h5>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={acctCharts.petty_cash_utilization} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="fund_name" />
                    <YAxis />
                    <Tooltip formatter={currencyTooltipFormatter} />
                    <Legend />
                    <Bar dataKey="total_spent" fill={COLORS[5]} name="Spent" />
                    <Bar dataKey="balance" fill={COLORS[1]} name="Remaining" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {acctCharts.gl_account_balances?.length > 0 && (
              <div className="bg-white shadow-md rounded-lg p-6 h-full">
                <h5 className="text-base font-semibold text-gray-700 mb-4">GL Account Balances (Top 15)</h5>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={acctCharts.gl_account_balances.slice(0, 15)} layout="vertical" margin={{ top: 5, right: 20, left: 80, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={currencyTooltipFormatter} />
                    <Bar dataKey="balance" fill={COLORS[4]} name="Balance" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportsDashboard;
